/**
 * 時間の錨 (TimeAnchor) のスキーマ (docs/features/video-editor-timeline-lineage-refactoring-plan.md §4.1)。
 *
 * ナレーション / 効果音 / BGM / 動画全体のオーバーレイは「動画全体の絶対秒」(startSec / endSec) で
 * 置かれているが、その**正は錨**で、絶対秒は錨から引き直したキャッシュ (保存時に必ず最新に直す。
 * レンダラーと書き出しは絶対秒だけを読む)。引き直しの規則は schema/timeAnchor.ts (§4.2)。
 *
 * スキーマだけを独立させているのは、schema/overlay.ts (project.ts が読み込む) からも参照するため
 * (関数側は remotion/layout.ts を読み込むので循環を避ける)。
 */
import { z } from 'zod';

export const TimeAnchorSchema = z.discriminatedUnion('kind', [
  // 項目に付く。itemId の項目の表示開始秒 + offsetSec が置き場所。offsetSec は 0〜項目の表示尺に収める
  z.object({ kind: z.literal('item'), itemId: z.string().min(1), offsetSec: z.number() }),
  // 動画の終わりからの距離 (最後の採用中シーンの終了秒 + offsetSec。offsetSec >= 0)
  z.object({ kind: z.literal('tail'), offsetSec: z.number().nonnegative() }),
  // 絶対秒に固定 (錨の先の項目が**削除**されたときの恒久的な退避先。§4.5)
  z.object({ kind: z.literal('absolute') }),
]);
export type TimeAnchor = z.infer<typeof TimeAnchorSchema>;

/**
 * 旧 `AudioCue.anchor` (予約欄 `{itemId, offsetSec}`・kind なし) を新しい形に読み替える前処理。
 * FE では書いていなかった欄なので実データには無いはずだが、保険として `item` 錨に直す。
 */
export function migrateLegacyAnchorShape(value: unknown): unknown {
  if (value && typeof value === 'object' && !('kind' in value) && 'itemId' in value) {
    const legacy = value as { itemId: unknown; offsetSec?: unknown };
    if (typeof legacy.itemId === 'string' && legacy.itemId.length > 0) {
      return { kind: 'item', itemId: legacy.itemId, offsetSec: typeof legacy.offsetSec === 'number' ? legacy.offsetSec : 0 };
    }
  }
  return value;
}

/** 入口で旧形を読み替える錨のスキーマ (省略可)。 */
export const TimeAnchorFieldSchema = z.preprocess(migrateLegacyAnchorShape, TimeAnchorSchema).optional();

/** 2 つの錨が同じか (値の比較)。 */
export function timeAnchorsEqual(a: TimeAnchor | undefined, b: TimeAnchor | undefined): boolean {
  if (a === b) return true;
  if (!a || !b || a.kind !== b.kind) return false;
  if (a.kind === 'absolute') return true;
  if (a.kind === 'tail') return b.kind === 'tail' && Math.abs(a.offsetSec - b.offsetSec) < 1e-9;
  return b.kind === 'item' && a.itemId === b.itemId && Math.abs(a.offsetSec - b.offsetSec) < 1e-9;
}
